import React, { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { SUPPORTED_LANGUAGES } from '../../constants/languages'

const CloseIcon = () => (
    <svg
        className="w-5 h-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
)

interface GuestLanguageModalProps {
    isOpen: boolean
    onClose: () => void
    onSelectLanguage: (code: string) => void
}

export function GuestLanguageModal({ isOpen, onClose, onSelectLanguage }: GuestLanguageModalProps) {
    // Lock body scroll when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden'
            return () => {
                document.body.style.overflow = 'unset'
            }
        }
    }, [isOpen])

    if (!isOpen) return null

    return createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="flex min-h-full items-center justify-center p-4">
                <div
                    className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="p-6 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                        <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                            Choose a language
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 transition-colors"
                        >
                            <CloseIcon />
                        </button>
                    </div>

                    <div className="p-6 grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
                        {SUPPORTED_LANGUAGES.map((lang) => (
                            <button
                                key={lang.code}
                                onClick={() => onSelectLanguage(lang.code)}
                                className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary-500 dark:hover:border-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-all group text-left relative"
                            >
                                <span className="text-2xl">{lang.flag}</span>
                                <div className="flex-1">
                                    <div className="font-semibold text-gray-900 dark:text-white group-hover:text-primary-700 dark:group-hover:text-primary-400">
                                        {lang.name}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {lang.nativeName}
                                    </div>
                                    {lang.ttsSupport === false && (
                                        <div className="flex items-center gap-1 mt-1 text-xs text-amber-600 dark:text-amber-400">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                            </svg>
                                            Text only
                                        </div>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 text-center text-sm text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700">
                        Select a language to start your free trial conversation
                    </div>
                </div>
            </div>
        </div>,
        document.body
    )
}
